import { useCallback, useEffect, useRef, useState } from "react";

import type { Session, SessionApi } from "../lib/api";

/** What the candidate needs to know about their own session.
 *
 * One place, always visible: time spent, time left, how much of the assistant's
 * allowance is gone, and the way to hand in. These were scattered across a
 * status bar and a panel header, which meant none of them were noticed.
 *
 * The clock counts **active** time and it is kept by the server. Closing the
 * tab stops it, so a take-home left open overnight does not expire unread, and
 * Pause stops it deliberately. What it deliberately does not do is guess at
 * presence from focus or visibility: reading the brief in another window is
 * working, and a clock that stops when you look away is one the candidate
 * cannot reason about. A clock the candidate's own page owns would not be a
 * clock at all, so the server keeps it.
 */

const HEARTBEAT_MS = 15_000;
const WARN_AT_MS = 10 * 60_000;

function clock(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return h > 0
    ? `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`
    : `${m}:${String(sec).padStart(2, "0")}`;
}

export function SessionPanel({
  api,
  session,
  onSession,
  unsaved,
}: {
  api: SessionApi;
  session: Session;
  onSession: (s: Session) => void;
  unsaved: number;
}) {
  // Server truth, advanced locally between heartbeats so the display ticks.
  const [elapsed, setElapsed] = useState(session.elapsed_active_ms);
  const [budgetMs, setBudgetMs] = useState((session.time_budget_minutes ?? 0) * 60_000);
  const [submitting, setSubmitting] = useState(false);
  const [autoSubmitted, setAutoSubmitted] = useState(false);
  const [paused, setPaused] = useState(session.paused_at !== null);
  // A live interview is a scheduled hour with someone on the call. The server
  // refuses it too; this only keeps the button from being there to press.
  const canPause = session.mode !== "live";
  const [pausing, setPausing] = useState(false);
  const closed = session.state === "submitted" || session.state === "expired";
  const closedRef = useRef(closed);
  closedRef.current = closed;

  const beat = useCallback(async () => {
    if (closedRef.current) return;
    try {
      const h = await api.heartbeat();
      setElapsed(h.elapsed_active_ms);
      setBudgetMs(h.budget_ms);
      // Server truth: another tab may have paused, and pause freezes writes.
      setPaused(h.paused);
      if (h.paused !== (session.paused_at !== null)) onSession(await api.session());
      if (h.state === "submitted" || h.state === "expired") {
        setAutoSubmitted(Boolean(h.auto_submitted));
        onSession(await api.session());
      }
    } catch {
      // A missed beat costs nothing: the server credits the real gap next time.
    }
  }, [api, onSession, session.paused_at]);

  useEffect(() => {
    void beat();
    const t = setInterval(beat, HEARTBEAT_MS);
    return () => clearInterval(t);
  }, [beat]);

  // Between heartbeats the number still has to move, or it looks frozen.
  useEffect(() => {
    if (closed || paused) return;
    const t = setInterval(() => setElapsed((e) => e + 1000), 1000);
    return () => clearInterval(t);
  }, [closed, paused]);

  // A tab about to close should not lose the last stretch of work.
  useEffect(() => {
    function flush() {
      if (!closedRef.current) void api.heartbeat().catch(() => {});
    }
    window.addEventListener("pagehide", flush);
    return () => window.removeEventListener("pagehide", flush);
  }, [api]);

  const remaining = budgetMs ? Math.max(0, budgetMs - elapsed) : null;
  const warning = remaining !== null && remaining <= WARN_AT_MS && remaining > 0 && !closed;

  const used = session.agent_tokens_billable;
  const cap = session.agent_token_budget || 0;
  const tokenPct = cap ? Math.min(100, (used / cap) * 100) : 0;

  async function togglePause() {
    setPausing(true);
    try {
      const next = await (paused ? api.resume() : api.pause());
      setPaused(next.paused);
      setElapsed(next.elapsed_active_ms);
      onSession(await api.session());
    } finally {
      setPausing(false);
    }
  }

  async function submit() {
    const note = unsaved
      ? `\n\nYou have ${unsaved} unsaved file${unsaved === 1 ? "" : "s"}. Save them first if you want them counted.`
      : "";
    if (
      !window.confirm(
        "Submit this session?\n\nYour work is recorded as it stands now. You will " +
          "not be able to edit files or use the terminal afterwards." +
          note,
      )
    )
      return;
    setSubmitting(true);
    try {
      onSession(await api.submit());
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="session-panel" aria-label="Session">
      <div className="pane-title">
        <span>Session</span>
        {closed && <span className="pill-done">submitted</span>}
      </div>

      {warning && (
        <p className="time-warning" role="status">
          <b>{clock(remaining!)} left.</b> The session submits itself when the time
          is up — save anything you want counted.
        </p>
      )}
      {paused && !closed && (
        <p className="time-warning paused" role="status">
          <b>Paused.</b> Your clock is stopped, and so are the editor, the
          terminal and the assistant. Resume when you are ready.
        </p>
      )}
      {closed && autoSubmitted && (
        <p className="time-warning done" role="status">
          Time was up, so this session was submitted for you. Your work is saved.
        </p>
      )}

      <dl className="session-stats">
        <div>
          <dt>Time spent</dt>
          <dd className="numeric">{clock(elapsed)}</dd>
        </div>
        {remaining !== null && (
          <div>
            <dt>Remaining</dt>
            <dd className={`numeric${warning ? " urgent" : ""}`}>{clock(remaining)}</dd>
          </div>
        )}
      </dl>

      {remaining !== null && budgetMs > 0 && (
        <div className="bar" title="Counts only while this tab is open">
          <span
            className={warning ? "urgent" : ""}
            style={{ width: `${Math.min(100, (elapsed / budgetMs) * 100)}%` }}
          />
        </div>
      )}

      {cap > 0 && (
        <>
          <dl className="session-stats">
            <div>
              <dt>AI assist used</dt>
              <dd className="numeric">{tokenPct.toFixed(0)}%</dd>
            </div>
          </dl>
          <div className="bar" title={`${used.toLocaleString()} of ${cap.toLocaleString()} tokens`}>
            <span className={tokenPct >= 90 ? "urgent" : ""} style={{ width: `${tokenPct}%` }} />
          </div>
        </>
      )}

      <div className="session-actions">
        {canPause && (
          <button className="pause" disabled={pausing || closed} onClick={togglePause}>
            {pausing ? "…" : paused ? "Resume" : "Pause"}
          </button>
        )}
        <button className="hand-in" disabled={submitting || closed} onClick={submit}>
          {closed ? "Submitted" : submitting ? "Submitting…" : "Submit work"}
        </button>
      </div>
    </section>
  );
}
